import { useCallback, useEffect, useMemo, useRef } from 'react';

import { TabType } from '@/components/Types';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import {
  ActionTypeManageFCupEnum,
  requestPostulantManagementFCup,
  requestPostulationFCup,
} from '@/services/friendsCup';
import { ActionsTourneys, GenericItemTourneysData } from '@/types/tda';
import { ERROR_STATUS_FCUP, TEXTS } from '@/utils';

export const useHandleInvitations = (
  list: GenericItemTourneysData[] | null,
  tab = TabType.FirstTab
) => {
  const toast = useToast();
  const { updateTourney, tourneyData } = useTourney();
  const listRef = useRef<GenericItemTourneysData[] | null>(null);

  useEffect(() => {
    if (!list) return;
    listRef.current = list;
  }, [list]);

  const textComplementary = useMemo(() => {
    return tab === TabType.FirstTab ? 'al Equipo' : 'al Equipo de';
  }, [tab]);

  const onHandlePendingPostulation = useCallback(
    async (id: number, name: string) => {
      try {
        const responseManagePostulant = await requestPostulantManagementFCup({
          tipoAccion: ActionTypeManageFCupEnum.ACC_ACEPTAR_INVITACION,
          idGrupo: id,
        });

        if (responseManagePostulant.estado) {
          if (tab === TabType.FirstTab) {
            toast.show(
              `Tenías una invitación pendiente para el equipo ${name}. Ya estás participando.`
            );
          } else {
            toast.show(
              `Tenías una invitación pendiente para el equipo de ${name}. Ya estás participando.`
            );
          }
        } else {
          console.error(responseManagePostulant.mensaje);
          toast.show('Error al invitar');
        }
      } catch (error) {
        console.error(error);
        toast.show(TEXTS.defaultError);
      }
    },
    [toast, tab]
  );

  const onHandleSubmit = useCallback(
    async ({ id }: ActionsTourneys) => {
      try {
        if (!listRef.current) return;

        const group = listRef.current.find((t) => t.id === id);
        if (!group) return; // Add check for group existence

        const response = await requestPostulationFCup(id);

        if (response.estado) {
          toast.show(
            `Enviaste tu solicitud para unirte ${textComplementary} ${group?.title}`
          );
          // set refresh and pending list ids
          updateTourney({
            ...tourneyData,
            idsPendings: [...(tourneyData.idsPendings || []), id],
          });
        } else {
          if (
            response.codError &&
            response.codError === ERROR_STATUS_FCUP.PENDING_INVITATION
          ) {
            await onHandlePendingPostulation(id, String(group.title));

            // set refresh after invitacion
            updateTourney({
              ...tourneyData,
              idsPlayings: [...(tourneyData.idsPlayings || []), id],
            });
          } else if (
            response.mensaje &&
            response.codError !== ERROR_STATUS_FCUP.PENDING_INVITATION
          ) {
            toast.show(response.mensaje);
          }
        }
      } catch (err) {
        console.error('handleInv:' + err);
        toast.show(TEXTS.defaultError);
        throw new Error(TEXTS.defaultError);
      }
    },
    [
      updateTourney,
      onHandlePendingPostulation,
      textComplementary,
      toast,
      tourneyData,
    ]
  );

  return { onHandleSubmit };
};
